import { useEffect, type ReactNode } from 'react';

export type AdminConfirmDialogProps = {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** danger : bouton principal en rouge (suppression, etc.) */
  variant?: 'danger' | 'neutral';
  isConfirming?: boolean;
  errorMessage?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  /** id pour aria-labelledby */
  titleId?: string;
  /** Si false : un seul bouton (ex. message d’information). */
  showCancel?: boolean;
  confirmDisabled?: boolean;
};

/**
 * Modale de confirmation admin (remplace window.confirm) — overlay, focus piégé minimal, annulation au clic extérieur.
 */
export default function AdminConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  variant = 'neutral',
  isConfirming = false,
  errorMessage = null,
  onConfirm,
  onCancel,
  titleId = 'admin-confirm-dialog-title',
  showCancel = true,
  confirmDisabled = false,
}: AdminConfirmDialogProps) {
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isConfirming) {
        if (showCancel) {
          onCancel();
        } else {
          onConfirm();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isConfirming, onCancel, onConfirm, showCancel]);

  if (!open) {
    return null;
  }

  const confirmClasses =
    variant === 'danger'
      ? 'bg-error text-on-error hover:opacity-95 focus-visible:ring-error'
      : 'bg-primary text-on-primary hover:opacity-95 focus-visible:ring-primary';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isConfirming) {
          if (showCancel) {
            onCancel();
          } else {
            onConfirm();
          }
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="text-lg font-black text-primary">
          {title}
        </h2>
        {description ? (
          <div className="mt-3 text-sm leading-relaxed text-on-surface-variant">{description}</div>
        ) : null}
        {errorMessage ? (
          <p className="mt-4 rounded-md border border-error/30 bg-error/10 px-3 py-2 text-sm font-medium text-error">
            {errorMessage}
          </p>
        ) : null}
        <div
          className={`mt-6 flex gap-2 ${showCancel ? 'flex-col-reverse sm:flex-row sm:justify-end' : 'justify-center'}`}
        >
          {showCancel ? (
            <button
              type="button"
              onClick={onCancel}
              disabled={isConfirming}
              className="rounded-md border border-outline-variant/30 px-4 py-2.5 text-sm font-bold text-on-surface transition-colors hover:bg-surface-container-high disabled:opacity-50"
            >
              {cancelLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onConfirm}
            disabled={isConfirming || confirmDisabled}
            className={`rounded-md px-4 py-2.5 text-sm font-bold shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 ${confirmClasses}`}
          >
            {isConfirming ? 'Veuillez patienter…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
